<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import IconButton from '../IconButton.vue';
    import { UsersTableColumn, UsersTableItem } from '@/js/stores/users-table';
    import UserCell from '../coaching/table/UserCell.vue';
    import { useRoute } from 'vue-router';
    import { dateToString } from '@/js/utils/dateUtils';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, UserManagementPermissionFlag } from '@/js/consts/permissions';
    import { PencilIcon, TrashIcon, ShieldAlertIcon } from '@lucide/vue';
    const { hasPermission } = usePermissions();

    const route = useRoute();

    defineProps<{
        column: UsersTableColumn['field'];
        data: UsersTableItem;
    }>();

    defineEmits<{
        (e: 'delete', user: UsersTableItem): void;
    }>();

    const hasUserAccess = hasPermission(
        PermissionArea.UserManagement,
        UserManagementPermissionFlag.Access
    );
</script>

<template>
    <div class="h-10 flex items-center">
        <template v-if="column === 'name'">
            <div>
                <UserCell :label="data.name" color="avocado" />
            </div>
        </template>
        <template v-else-if="column === 'role'">
            <div>
                <p class="text-body">
                    {{ data.role }}
                </p>
            </div>
        </template>
        <template v-else-if="column === 'lastSeen'">
            <p class="text-body">
                {{ data.lastSeen ? dateToString(data.lastSeen) : '' }}
            </p>
        </template>
        <template v-else-if="column === 'hasOverride'">
            <span v-if="data.hasOverride" class="inline-flex items-center gap-1.5 text-body">
                <ShieldAlertIcon class="size-4 text-primary-carrot shrink-0" />
                {{ __('yes') }}
            </span>
            <span v-else class="text-body text-gray">
                {{ __('no') }}
            </span>
        </template>
        <template v-else-if="column === 'actions'">
            <div v-if="hasUserAccess" class="flex gap-3 items-center pr-4">
                <IconButton
                    size="size-6"
                    area-size="size-6"
                    :tooltip="__('edit')"
                    :route="{ path: `/settings/manage-users/${data.id}/edit`, query: route.query }"
                >
                    <PencilIcon class="size-5" />
                </IconButton>
                <IconButton
                    size="size-6"
                    area-size="size-6"
                    :tooltip="__('delete')"
                    @click="$emit('delete', data)"
                >
                    <TrashIcon class="size-5" />
                </IconButton>
            </div>
        </template>
    </div>
</template>
